import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Input, ListGroup, Card, Button } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { format } from 'date-fns';

import {
  agentListGet,
  chatHistoryGet,
  getChatCount,
  markAsRead,
  sendChatMsg,
  setActiveChatDetails,
  supervisorChatListGet,
  useActiveChatDetails,
  useAgentList,
  useCountList,
  useSupervisorChatList
} from '../../store/slices/tenantSlices/chatSlice';
import { useUserDetails } from '../../store/slices/authSlice';
import AttachmentModal from './AttachmentModal';
import { socket } from '../../config/Socket';
import { getFileExtension } from '../../utils/common';
import PreviewModal from './PreviewModal';
import config from '../../config';
import { useTranslation } from 'react-i18next';

// At the top of your file
const userImages = [
  require('../../assets/images/users/user1.jpg'),
  require('../../assets/images/users/user2.jpg'),
  require('../../assets/images/users/user3.jpg'),
  require('../../assets/images/users/user4.jpg'),
  require('../../assets/images/users/user5.jpg')
];

const Chat = () => {
  const dispatch = useDispatch();
  const user = useUserDetails();
  const { t } = useTranslation();

  const agentList = useAgentList();
  const supervisorChatList = useSupervisorChatList();
  const countList = useCountList();
  const activeChatDetails = useActiveChatDetails();

  const [activeTab, setActiveTab] = useState(
    user?.process_details?.chat_with_user ? 'agent' : 'supervisor'
  );
  const [message, setMessage] = useState('');
  const [attachModal, setAttachModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewUrl, setIsPreviewUrl] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);

  useEffect(() => {
    setActiveTab(user?.process_details?.chat_with_user ? 'agent' : 'supervisor');
  }, [user]);

  const handleSend = async (file = undefined) => {
    if ((file ? false : !message?.trim()) || isLoading) return false;
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('fromUserId', user?.user_id);
      formData.append('toUserId', activeChatDetails?.id);
      formData.append('message', message);
      formData.append('fromRole', 'agent');
      formData.append('toRole', activeChatDetails?.activeTab);

      if (file) {
        formData.append('file', file);
      }

      await dispatch(sendChatMsg(formData)).unwrap();
      fetchChatHistory(activeChatDetails);
      setMessage('');
      setAttachModal(false);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('error', error);
    }
  };

  const fetchAgentList = async () => {
    try {
      await dispatch(agentListGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchSupervisorChatList = async () => {
    try {
      await dispatch(supervisorChatListGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchAgentList();
    fetchSupervisorChatList();
  }, [activeTab]);

  const fetchChatHistory = async (activeChatDetails) => {
    try {
      const payload = {
        fromUserId: user?.user_id,
        toUserId: activeChatDetails?.id,
        fromRole: 'agent',
        toRole: activeChatDetails?.activeTab
      };
      const response = await dispatch(chatHistoryGet(payload)).unwrap();
      setChatHistory(response?.data?.reverse() || []);
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchChatCount = async () => {
    try {
      const payload = {
        userId: user?.user_id,
        userRole: 'agent'
      };
      await dispatch(getChatCount(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onMarkAsRead = async (countObj) => {
    try {
      const payload = {
        toUserId: user?.user_id,
        toRole: 'agent',
        fromUserId: countObj?.fromUserId,
        fromRole: countObj?.fromRole
      };
      await dispatch(markAsRead(payload)).unwrap();
      fetchChatCount();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (activeChatDetails) {
      fetchChatHistory(activeChatDetails);
    }
  }, [activeChatDetails]);

  const getFileName = (path) => {
    const pathnames = path?.split('/');
    return pathnames?.[pathnames.length - 1];
  };

  useEffect(() => {
    const handleChatMessage = (message) => {
      console.log('message------->', message);
      if (message?.fromUserId === activeChatDetails?.id) {
        onMarkAsRead({ ...message });
        fetchChatHistory({ id: message?.fromUserId, activeTab: activeTab });
      } else {
        fetchChatCount();
      }
    };

    socket.on('chatMessage', handleChatMessage);

    return () => {
      socket.off('chatMessage', handleChatMessage);
    };
  }, [activeTab, activeChatDetails]);

  const fileDownload = async (url) => {
    try {
      const response = await fetch(url, {
        mode: 'cors' // or 'no-cors' if needed — but only if you're not accessing the response body
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;

      // Get filename from URL
      const filename = url.split('/').pop();
      link.download = filename || 'download';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the object URL after use to free up memory
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const tabs = [
    { label: t('users'), key: 'agent', condition: user?.process_details?.chat_with_user },
    {
      label: t('supervisor'),
      key: 'supervisor',
      condition: user?.process_details?.chat_with_supervisor
    }
  ];

  const visibleTabs = tabs.filter((tab) => tab.condition).length;
  const tabWidth = visibleTabs === 2 ? 'w-50' : 'w-100';

  // Function to get count for a specific role
  const getCountForRole = (role) => {
    const roleData = countList.filter((item) => item._id.fromRole === role);
    return roleData ? roleData.length : 0; // Return 0 if no data is found for the role
  };

  return (
    <Container fluid className="chat-app">
      <Row className="h-100">
        <Col md="3" className="chat-app__sidebar">
          <div className="d-flex align-items-center">
            {tabs.map(
              (tab) =>
                tab.condition && (
                  <span
                    key={tab.key}
                    className={`text-capitalize d-flex gap-2 justify-content-center text-center p-2 cursor-pointer ${tabWidth} ${activeTab === tab.key ? 'border-bottom border-1 border-white fw-bolder text-white' : 'text-lightGray'}`}
                    onClick={() => {
                      setActiveTab(tab.key);
                      dispatch(setActiveChatDetails(null));
                    }}>
                    {tab.label}{' '}
                    {getCountForRole(tab.key) > 0 && (
                      <label className="bg-info rounded-circle msg-badge">
                        {getCountForRole(tab.key)}
                      </label>
                    )}
                  </span>
                )
            )}
          </div>
          <ListGroup flush>
            {(activeTab === 'agent' ? agentList : supervisorChatList).map((val, index) => {
              const randomImage = userImages[val.id % userImages.length];
              const countObj = countList?.find((x) => x?._id?.fromUserId === val?.id) || null;
              return (
                <div
                  key={index}
                  className={`chat-user ${activeChatDetails?.id === val?.id && activeChatDetails?.name === val?.name ? 'active-chat-user' : ''}`}
                  onClick={() => {
                    if (countObj) {
                      onMarkAsRead(countObj?._id);
                    }
                    dispatch(
                      setActiveChatDetails({ ...val, profile: randomImage, activeTab: activeTab })
                    );
                  }}>
                  <div className="position-relative">
                    <img src={randomImage} alt="profile" className="rounded-circle" width="40" />
                    <div
                      className={`${val?.isActive ? 'bg-success' : 'bg-danger'} user-status`}></div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <label>{val?.name}</label>
                    {countObj && (
                      <label className="bg-info rounded-circle msg-badge">{countObj?.count}</label>
                    )}
                  </div>
                </div>
              );
            })}
          </ListGroup>
        </Col>

        {activeChatDetails ? (
          <Col md="9" className="chat-app__main d-flex flex-column">
            <div className="chat-app__header d-flex align-items-center">
              <span>
                <img
                  src={activeChatDetails?.profile}
                  alt="profile"
                  className="rounded-circle"
                  width="28"
                />
              </span>
              <strong className="text-capitalize ms-2">{activeChatDetails.name}</strong>
            </div>
            <div className="chat-app__messages flex-grow-1 p-3 overflow-auto">
              {chatHistory.map((msg, idx) => {
                const isSentByCurrentUser = msg.fromUserId === user?.user_id;
                const formattedTime = format(new Date(msg.timestamp), 'hh:mm a');
                const extension = getFileExtension(msg?.filePath);
                return (
                  <div
                    key={msg._id}
                    className={`chat-app__message-wrapper ${isSentByCurrentUser ? 'sent' : 'received'}`}>
                    <div className={`chat-app__message ${msg?.filePath ? 'chat-app__media' : ''}`}>
                      {msg?.filePath ? (
                        <div className="chat-app__message-text">
                          {extension === 'pdf' ? (
                            <div>
                              {msg?.fileName ? msg?.fileName : getFileName(msg?.filePath)}
                              <div className="media-icon">
                                <Icon.FileText size={40} />
                              </div>
                              <div className="w-100 d-flex align-items-center gap-1">
                                <div className="w-50">
                                  <Button
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() =>
                                      setIsPreviewUrl(`${config.BASE_URL}${msg?.filePath}`)
                                    }>
                                    {t('open')}
                                  </Button>
                                </div>
                                <div className="w-50">
                                  <Button
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => {
                                      fileDownload(`${config.BASE_URL}${msg?.filePath}`);
                                    }}>
                                    {t('download')}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {msg?.fileName ? msg?.fileName : getFileName(msg?.filePath)}
                              <div className="image-wrapper">
                                <img src={`${config.BASE_URL}${msg?.filePath}`} />
                              </div>
                              <div className="w-100 d-flex align-items-center gap-1">
                                <div className="w-50">
                                  <Button
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() =>
                                      setIsPreviewUrl(`${config.BASE_URL}${msg?.filePath}`)
                                    }>
                                    {t('open')}
                                  </Button>
                                </div>
                                <div className="w-50">
                                  <Button
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => {
                                      fileDownload(`${config.BASE_URL}${msg?.filePath}`);
                                    }}>
                                    {t('download')}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="chat-app__message-text">{msg.message}</div>
                      )}
                      <div className="chat-app__message-time">{formattedTime}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="chat-app__input d-flex align-items-center p-2 border-top">
              <div
                className="ms-2 pe-2 text-primary cursor-pointer"
                onClick={() => setAttachModal(true)}>
                <Icon.Paperclip size={20} />
              </div>
              <Input
                type="text"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              />
              <div className="ms-2 pe-2 text-info" onClick={() => handleSend()}>
                <Icon.Send size={20} />
              </div>
            </div>
          </Col>
        ) : (
          <Col md="9">
            <Card className="p-0 d-flex justify-content-center align-items-center no-data m-0 h-100">
              {`Please select a ${activeTab === 'agent' ? 'user' : 'supervisor'} to start chatting.`}
            </Card>
          </Col>
        )}
      </Row>
      {attachModal && (
        <AttachmentModal
          isOpen={attachModal}
          handleCancel={() => setAttachModal(false)}
          handleSubmit={(file) => handleSend(file)}
          isLoading={isLoading}
        />
      )}
      {isPreviewUrl && (
        <PreviewModal
          isOpen={isPreviewUrl}
          url={isPreviewUrl}
          handleCancel={() => setIsPreviewUrl(null)}
        />
      )}
    </Container>
  );
};

export default Chat;
